import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { EditorCommand } from '@atlaskit/editor-common/types';
import type { PaletteColor } from '@atlaskit/editor-common/ui-color';
import {
  highlightColorPalette,
  REMOVE_HIGHLIGHT_COLOR,
  textColorPalette,
} from '@atlaskit/editor-common/ui-color';
import {
  ColorPalette,
  ToolbarButton,
  ToolbarSection,
  ToolbarTooltip,
} from '@atlaskit/editor-toolbar';
import Popup from '@atlaskit/popup';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { type DatabasesEditorAPI } from '../databases-preset.ts';

import { TextColorPickerIcon } from './text-color-picker-icon.tsx';
import {
  DATABASES_FORMATTING_TOOLBAR_POPUP_TEST_ID,
  getHighlightPaletteColors,
  TEXT_COLOR_PALETTE_COLS,
} from './toolbar-constants.ts';

const colorButtonWrapperStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const colorPickerContentStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space.150',
  padding: 'space.150',
});

const colorPickerSectionStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space.100',
});

const colorPickerSectionLabelStyles = xcss({
  font: 'font.heading.xsmall',
  fontWeight: 'font.weight.bold',
  color: 'color.text',
});

const highlightSwatchRowStyles = xcss({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'space.050',
});

const highlightSwatchStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: 'radius.medium',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border.inverse',
  backgroundColor: 'color.background.neutral.subtle',
  padding: 'space.025',
  ':hover': {
    borderColor: 'color.border',
  },
});

const removeColorButtonStyles = xcss({
  width: '100%',
  minHeight: '32px',
  paddingBlock: 'space.0',
  paddingInline: 'space.200',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
  borderRadius: 'radius.medium',
  backgroundColor: 'color.background.neutral.subtle',
  cursor: 'pointer',
  font: 'font.body',
  fontWeight: 'font.weight.medium',
  color: 'color.text',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered',
  },
});

interface ColorPickerSectionProps {
  editorApi: DatabasesEditorAPI;
  textColorState:
    | {
        color?: string | null;
        defaultColor?: string;
        disabled?: boolean;
        palette?: PaletteColor[];
      }
    | undefined;
  highlightState:
    | {
        activeColor?: string | null;
        disabled?: boolean;
        palette?: unknown;
      }
    | undefined;
  execute: ((command: EditorCommand | undefined) => boolean) | undefined;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  preventBlur: React.MouseEventHandler;
}

export const ColorPickerSection: React.FC<ColorPickerSectionProps> = ({
  editorApi,
  textColorState,
  highlightState,
  execute,
  isOpen,
  onToggle,
  onClose,
  preventBlur,
}) => {
  const { formatMessage } = useIntl();
  const activeColor = textColorState?.color ?? textColorState?.defaultColor ?? null;
  const palette = textColorState?.palette ?? textColorPalette;
  const highlightPalette = highlightState?.palette ?? highlightColorPalette;
  const highlightPaletteColors = useMemo(
    () => getHighlightPaletteColors(highlightPalette),
    [highlightPalette],
  );

  const changeTextColor = useCallback(
    (color: string) => {
      execute?.(editorApi?.textColor?.commands.changeColor(color, INPUT_METHOD.TOOLBAR));
      onClose();
    },
    [execute, editorApi, onClose],
  );

  const changeHighlightColor = useCallback(
    (color: string) => {
      execute?.(
        editorApi?.highlight?.commands.changeColor({
          color,
          inputMethod: INPUT_METHOD.TOOLBAR,
        }),
      );
      onClose();
    },
    [execute, editorApi, onClose],
  );

  const removeColor = useCallback(() => {
    const defaultTextColor = textColorState?.defaultColor;
    if (defaultTextColor) {
      execute?.(
        editorApi?.textColor?.commands.changeColor(
          defaultTextColor,
          INPUT_METHOD.TOOLBAR,
        ),
      );
    }
    execute?.(
      editorApi?.highlight?.commands.changeColor({
        color: REMOVE_HIGHLIGHT_COLOR,
        inputMethod: INPUT_METHOD.TOOLBAR,
      }),
    );
    onClose();
  }, [execute, editorApi, textColorState?.defaultColor, onClose]);

  return (
    <ToolbarSection>
      <Popup
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom-start"
        autoFocus={false}
        shouldDisableFocusLock
        trigger={(triggerProps) => (
          <Box xcss={colorButtonWrapperStyles}>
            <ToolbarTooltip
              content={formatMessage(
                messages['rich-text-formatting-toolbar.text-and-highlight-color'],
              )}
              position="top"
            >
              <ToolbarButton
                {...triggerProps}
                iconBefore={
                  <TextColorPickerIcon
                    iconColor={activeColor ?? undefined}
                    backgroundColor={
                      highlightState?.activeColor &&
                      highlightState.activeColor !== REMOVE_HIGHLIGHT_COLOR
                        ? highlightState.activeColor
                        : undefined
                    }
                    isDisabled={textColorState?.disabled}
                    label={formatMessage(
                      messages['rich-text-formatting-toolbar.text-color'],
                    )}
                  />
                }
                isDisabled={textColorState?.disabled}
                isSelected={isOpen}
                onClick={onToggle}
                testId="text-color-button"
              />
            </ToolbarTooltip>
          </Box>
        )}
        content={() => (
          <Box
            as="div"
            onMouseDown={preventBlur}
            testId={DATABASES_FORMATTING_TOOLBAR_POPUP_TEST_ID}
            xcss={colorPickerContentStyles}
          >
            <Box xcss={colorPickerSectionStyles}>
              <Box as="span" xcss={colorPickerSectionLabelStyles}>
                {formatMessage(messages['rich-text-formatting-toolbar.text-color'])}
              </Box>
              <ColorPalette
                paletteOptions={{ palette }}
                selectedColor={activeColor}
                onClick={(color) => changeTextColor(color)}
                cols={TEXT_COLOR_PALETTE_COLS}
              />
            </Box>
            <Box xcss={colorPickerSectionStyles}>
              <Box as="span" xcss={colorPickerSectionLabelStyles}>
                {formatMessage(messages['rich-text-formatting-toolbar.highlight-color'])}
              </Box>
              <Box xcss={highlightSwatchRowStyles}>
                {highlightPaletteColors.map((color) => (
                  <Pressable
                    key={color}
                    xcss={highlightSwatchStyles}
                    onClick={() => changeHighlightColor(color)}
                    testId={`highlight-swatch-${color}`}
                  >
                    <TextColorPickerIcon
                      backgroundColor={color}
                      isSelected={highlightState?.activeColor === color}
                      label={formatMessage(
                        messages['rich-text-formatting-toolbar.highlight-color'],
                      )}
                    />
                  </Pressable>
                ))}
              </Box>
            </Box>
            <Pressable
              xcss={removeColorButtonStyles}
              onClick={removeColor}
              testId="remove-color-button"
            >
              {formatMessage(messages['rich-text-formatting-toolbar.remove-color'])}
            </Pressable>
          </Box>
        )}
      />
    </ToolbarSection>
  );
};
